import { authService, type UserType } from "../services/auth";
import { decodeToken } from "./tokenUtils";

/** Preserve every client-side URL component when carrying a protected route
 * through login or agreement acceptance. Hashes are especially important for
 * case-viewer anchors and were previously silently dropped (#893). */
export const locationDestination = (
  location: { pathname?: string; search?: string; hash?: string } | null | undefined,
): string | null => {
  if (!location?.pathname) return null;
  return `${location.pathname}${location.search || ""}${location.hash || ""}`;
};

/** Decode the reviewer's allowed_cohorts claim out of the current access token.
 * Returns empty array if no token, no claim, or decode fails — callers should
 * fall back to a sensible default.
 */
export const reviewerAllowedCohorts = (): string[] => {
  const token = authService.getStoredAuth().token;
  if (!token) return [];
  const claims = decodeToken(token) as { allowed_cohorts?: unknown } | null;
  const cohorts = claims?.allowed_cohorts;
  return Array.isArray(cohorts) ? cohorts.filter((c): c is string => typeof c === "string") : [];
};

/** True when the reviewer's JWT also carries a non-empty allowed_projects
 * claim — i.e. admin has granted them additive annotator access. The
 * reviewer-only PrivateRoute gate skips its redirect in that case; the
 * backend still filters every project/case by allowed_projects, so letting
 * them through to /labeling, /worklist, /reporter is safe. */
export const reviewerHasProjectAccess = (): boolean => {
  const token = authService.getStoredAuth().token;
  if (!token) return false;
  const claims = decodeToken(token) as { allowed_projects?: unknown } | null;
  const projects = claims?.allowed_projects;
  return Array.isArray(projects) && projects.length > 0;
};

/** True when the reviewer may configure labeling projects. This is an action
 * capability, not a data wildcard: backend project grants still scope every
 * existing project the manager can inspect or change. */
export const reviewerCanManageLabeling = (): boolean => {
  const token = authService.getStoredAuth().token;
  if (!token) return false;
  const claims = decodeToken(token) as { capabilities?: unknown } | null;
  const capabilities = claims?.capabilities;
  return Array.isArray(capabilities) && capabilities.includes("manage_labeling");
};

/** Path the reviewer should land on by default — their first allowed cohort.
 * Returns null when no cohort is in scope, so the caller can route to a
 * sensible fallback (e.g. /worklist for non-reviewer paths).
 */
export const reviewerHomePath = (): string | null => {
  const first = reviewerAllowedCohorts()[0];
  return first ? `/review/cohort/${encodeURIComponent(first)}` : null;
};

/** Where a user lands right after login / agreement-accept / impersonate /
 * invite-claim. Single source of truth so adding a new user_type only
 * requires one edit. Pass `allowedCohorts` when the caller already has the
 * fresh list from a server response; otherwise the helper reads it from the
 * current JWT.
 */
export const postAuthDestination = (opts: {
  userType: UserType | string | null | undefined;
  allowedCohorts?: readonly string[];
  fallback?: string;
}): string => {
  const { userType, allowedCohorts, fallback = "/worklist" } = opts;
  if (userType === "annotator") return "/labeling-overview";
  if (userType === "reviewer") {
    if (reviewerCanManageLabeling()) return "/labeling-overview";
    const cohort = allowedCohorts?.[0] ?? reviewerAllowedCohorts()[0];
    if (cohort) return `/review/cohort/${encodeURIComponent(cohort)}`;
  }
  return fallback;
};
